import { InjectedTypes, ArticleElementTypes } from "types/injector";
import log from "utils/csi/injectorLogger";

/**
 * Does the element match the type.
 * @param from The type of item we're looking for
 * @param element The element we're checking
 * @param component The component we're checking
 * @param index the current index for logging purposes
 * @param alreadyInjected all of the already injected items
 */
const doesMatchType = function (
  from: InjectedTypes | ArticleElementTypes,
  element: Element,
  component: JSX.Element,
  index: number,
  alreadyInjected?: { type: InjectedTypes; afterIndex: number }[]
) {
  if (alreadyInjected && alreadyInjected.length) {
    const lastInjected = alreadyInjected[alreadyInjected.length - 1];

    // Any injected item
    if (from == InjectedTypes.Any && lastInjected?.afterIndex == index) {
      log.debug(`Found an injected item after position ${index}`);
      return true;
    }

    // Injected item of type
    const found = alreadyInjected.find((injected) => {
      return injected.afterIndex == index && injected.type == from;
    });
    if (found) {
      return true;
    }
  }

  // Heading
  if (
    from == ArticleElementTypes.Heading &&
    element instanceof HTMLHeadingElement
  ) {
    log.debug(`Found heading in position ${index}`);
    return true;
  }
  // Figure
  if (
    from == ArticleElementTypes.Figure &&
    (element.tagName == "FIGURE" ||
      component.props.__typename == "ArticleInlineImage" ||
      component.props.__typename == "ArticleWellInlineImage" ||
      component.props.__typename == "ArticleOverflowInlineImage")
  ) {
    log.debug(`Found figure in position ${index}`);
    return true;
  }
  // Quote
  if (
    from == ArticleElementTypes.Quote &&
    (element.tagName == "BLOCKQUOTE" ||
      component.props.__typename == "ArticlePullquote")
  ) {
    log.debug(`Found quote in position ${index}`);
    return true;
  }
  // List
  if (
    from == ArticleElementTypes.List &&
    (element.tagName == "UL" || element.tagName == "OL")
  ) {
    log.debug(`Found list in position ${index}`);
    return true;
  }
  // HorizRule
  if (from == ArticleElementTypes.HorizRule && element.tagName == "HR") {
    log.debug(`Found horizontal rule in position ${index}`);
    return true;
  }
  // Bracketed Links
  if (
    from == ArticleElementTypes.BracketedLink &&
    component?.props.__typename == "ArticleRelatedContentLink"
  ) {
    return true;
  }
  // Legacy HTML, this must appear below any checks for element.tagName
  if (
    from == ArticleElementTypes.LegacyHtml &&
    component?.props.__typename == "ArticleLegacyHtml"
  ) {
    return true;
  }
  // Manually Placed Ads
  if (
    from == ArticleElementTypes.AdPlaceholder &&
    element.tagName == "GPT-AD"
  ) {
    return true;
  }
  // Manually Placed Recirc modules
  if (
    from == ArticleElementTypes.RecircPlaceholder &&
    component?.props.__typename == "ArticleRelatedContentModule"
  ) {
    return true;
  }
  // Dropcap Paragraph
  if (
    from == ArticleElementTypes.Dropcap &&
    component?.props.__typename == "ArticleParagraphContent" &&
    component?.props.subtype == "DROPCAP"
  ) {
    return true;
  }
  // Paragraph that ends with a comma or colon
  if (from == ArticleElementTypes.Salutations) {
    const text = ((element as HTMLElement).innerText || "").trim();

    if (
      component?.props.__typename == "ArticleParagraphContent" &&
      (text.endsWith(",") || text.endsWith(":"))
    ) {
      return true;
    }
  }

  return false;
};

export default doesMatchType;
